// @greenlight-spec: 29-app-environment-variables.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// Environment variables tab — user/managed two-column layout with reveal-on-demand.
// All mutation semantics (reserved-name rejection, manifest-presence warn,
// sensitive vault handling, audit reason) preserved exactly from docs/29.
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { AD_CARD, AD_CARD_HD } from '@/components/greenlight/surfaces';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';

import {
  AdEyebrow,
  EnvNameChip,
  TabErrorPanel,
  TabInlineEmpty,
  TabLoadingRows,
} from './tab-primitives';

// The dashboard doesn't prompt for a free-text reason per change; a fixed
// attribution keeps the audit trail honest ("set via the IT dashboard") without
// a modal on every edit. The MCP path carries the agent's own reason.
const DASHBOARD_REASON = 'Changed via the IT dashboard';

// ── EnvVarsTab ───────────────────────────────────────────────────────────────

export function EnvVarsTab({ appId }: { appId: string }) {
  const queryClient = useQueryClient();
  // Reveal is a read concern: envList?reveal=true returns plain (non-sensitive)
  // values on demand; sensitive values are never returned (docs/29 AC-ENV-03).
  const [reveal, setReveal] = React.useState(false);
  const [shown, setShown] = React.useState<Set<string>>(new Set());
  const [newName, setNewName] = React.useState('');
  const [newValue, setNewValue] = React.useState('');
  const [newSensitive, setNewSensitive] = React.useState(true);
  // The query string carries the explicit 'true'/'false' literal the contract
  // expects; 'false' must reach the server so values stay masked (docs/29 AC-ENV-03).
  // The cache key uses the same literal so it stays in lockstep with the payload.
  const revealParam = reveal ? 'true' : 'false';
  const { data, isLoading, isError } = api.env.list.useQuery(
    ['env', appId, { reveal: revealParam }],
    {
      params: { id: appId },
      query: { reveal: revealParam },
    },
  );
  const setMutation = api.env.set.useMutation();
  const removeMutation = api.env.remove.useMutation();

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['env', appId] });
  };

  const revealRow = (name: string) => {
    setReveal(true);
    setShown((s) => new Set(s).add(name));
  };

  const remove = async (name: string) => {
    try {
      const res = await removeMutation.mutateAsync({
        params: { id: appId, name },
        body: { reason: DASHBOARD_REASON },
      });
      if (res.status >= 300) throw res;
      toast.success(`${name} removed.`);
      // Stop displaying the removed name so a later re-add of the same name
      // doesn't surface a value without an explicit Reveal.
      setShown((s) => {
        const next = new Set(s);
        next.delete(name);
        return next;
      });
      await invalidate();
    } catch (err) {
      toast.error(`Remove failed: ${mutationErrorMessage(err)}`);
    }
  };

  const add = async () => {
    if (!newName || !newValue) return;
    try {
      const res = await setMutation.mutateAsync({
        params: { id: appId },
        body: { name: newName, value: newValue, sensitive: newSensitive, reason: DASHBOARD_REASON },
      });
      if (res.status >= 300) throw res;
      // The server resolves the manifest at the last-merge SHA, so its
      // present_in_manifest is authoritative — unlike the cached envList, which
      // can lag a merge that just added the name. An undeclared name is accepted
      // into the vault but won't reach the pod or appear in this tab until it's
      // declared in greenlight.yml and merged; warn rather than silently hide it.
      if (res.body.present_in_manifest) {
        toast.success(`${newName} set.`);
      } else {
        toast.warning(
          `${newName} saved, but it isn't declared in greenlight.yml yet — declare it there and merge for it to reach the app.`,
        );
      }
      setNewName('');
      setNewValue('');
      setNewSensitive(true);
      await invalidate();
    } catch (err) {
      toast.error(`Set failed: ${mutationErrorMessage(err)}`);
    }
  };

  if (isLoading) return <TabLoadingRows />;
  if (isError || !data || data.status !== 200)
    return <TabErrorPanel message="Couldn’t load env vars." />;

  const { user, managed } = data.body;

  return (
    <div data-testid="env-tab">
      {user.length === 0 && managed.length === 0 ? (
        <TabInlineEmpty message="No env vars declared." />
      ) : (
        // Two columns on lg: user-controlled left, managed right.
        // On smaller viewports both sections stack full-width.
        <div className="grid items-start gap-x-8 gap-y-6 lg:grid-cols-2 px-5 pt-4 pb-2">
          {/* USER-CONTROLLED column */}
          <section aria-label="User-controlled env vars" role="region">
            <AdEyebrow className="mb-3">User-controlled</AdEyebrow>
            {user.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">
                None declared in <MonoValue>greenlight.yml</MonoValue> yet.
              </p>
            ) : (
              <ul className="space-y-3">
                {user.map((e) => {
                  const isShown = shown.has(e.name);
                  return (
                    <li
                      key={e.name}
                      className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1"
                    >
                      <div className="min-w-0">
                        {/* Name chip — mono pill */}
                        <EnvNameChip name={e.name} />
                        {/* Value subline */}
                        <div className="mt-1 text-xs text-muted-foreground">
                          {e.sensitive ? 'Sensitive' : 'Plain'}
                          {' · '}
                          {e.present_in_vault ? 'value set' : 'no value set'}
                          {isShown && e.present_in_vault && (
                            <>
                              {' · '}
                              <MonoValue>{e.value ?? '(hidden — sensitive)'}</MonoValue>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex gap-1.5 shrink-0 items-center mt-0.5">
                        {e.present_in_vault && !e.sensitive && !isShown && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-11 md:h-7 text-[12px] px-2.5"
                            onClick={() => revealRow(e.name)}
                          >
                            Reveal
                          </Button>
                        )}
                        {e.present_in_vault && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-11 md:h-7 text-[12px] px-2.5 text-muted-foreground hover:text-foreground"
                            onClick={() => remove(e.name)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* MANAGED column */}
          <section aria-label="Managed env vars" role="region">
            <AdEyebrow className="mb-3">Managed — derived by Greenlight</AdEyebrow>
            {managed.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No managed variables.</p>
            ) : (
              <ul className="space-y-3">
                {managed.map((e) => (
                  <li key={e.name} className="flex items-center justify-between gap-3">
                    {/* Name chip */}
                    <EnvNameChip name={e.name} />
                    <span className="text-xs text-muted-foreground shrink-0">{e.source}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {/* Add form — full width below the two columns */}
      <div className="px-5 pt-4 pb-5 mt-2 border-t border-border/60 space-y-3">
        <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto] items-end">
          <Input
            aria-label="New env name"
            placeholder="ENV_NAME"
            value={newName}
            onChange={(e) => setNewName(e.target.value.toUpperCase())}
          />
          <Input
            aria-label="New env value"
            placeholder="value"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
          />
          <Button onClick={add}>Set</Button>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <Switch checked={newSensitive} onCheckedChange={setNewSensitive} aria-label="Sensitive" />
          Sensitive — store in the vault and never echo the value in plaintext lists.
        </label>
        <p className="text-xs text-muted-foreground">
          Setting an existing name overwrites its value, applied on the next merge. A name not yet
          declared in <MonoValue>greenlight.yml</MonoValue> is saved to the vault but won&rsquo;t
          reach the app until it&rsquo;s declared and merged. Managed names (e.g.{' '}
          <MonoValue>DATABASE_URL</MonoValue>) are derived by Greenlight and can&rsquo;t be set
          here.
        </p>
      </div>
    </div>
  );
}

// ── ConfigurationTab ─────────────────────────────────────────────────────────

/**
 * Environment variables — the user-controlled and managed names that change how
 * the app runs. Lifecycle actions (pause/restore/delete) live in the
 * header power menu (AppLifecycleMenu), not here.
 */
export function ConfigurationTab({ appId }: { appId: string }) {
  return (
    <div data-testid="configuration-tab">
      <div className={AD_CARD}>
        <div className={AD_CARD_HD}>
          <span className="text-sm font-semibold">Environment variables</span>
        </div>
        <EnvVarsTab appId={appId} />
      </div>
    </div>
  );
}
